/**
 * Mappers for transforming NSwag-generated API responses to canonical types.
 * NSwag generates classes with `| undefined` while canonical types use `| null`.
 */

import type {
  ProjectIndividualResponse as NSwagProjectIndividualResponse,
  ProjectDetailResponse as NSwagProjectDetailResponse,
  ProjectTaskDto as NSwagProjectTaskDto,
  PhaseDto as NSwagPhaseDto,
  ResourceDto as NSwagResourceDto,
  ContractDto as NSwagContractDto,
  ProjectListItem as NSwagProjectListItem,
  ProjectDetailResponseGroup as NSwagProjectDetailResponseGroup,
  Resource as NSwagResource,
} from "@lib/ShiOneClient";

import type {
  ProjectIndividual,
  ProjectInfo,
  Phase,
  ProjectTask,
  Resource,
  ProjectDetail,
  ProjectTaskGroup,
  Contract,
  DateValue,
} from "shared-ui/src/features/projects-feature/canonical";

function mapDate(date: Date | undefined): DateValue {
  if (!date) return null;
  return date;
}

export function mapToProjectInfo(
  item: NSwagProjectListItem | undefined,
): ProjectInfo | null {
  if (!item) return null;

  return {
    projectID: item.id ?? 0,
    projectName: item.projectName ?? "",
    status: item.status ?? null,
    accountID: item.accountID ?? null,
    accountName: item.accountName ?? null,
    contractID: item.contractID ?? null,
    startDateTime: mapDate(item.startDateTime),
    endDateTime: mapDate(item.endDateTime),
    projectLeadFirstName: item.projectLeadFirstName ?? null,
    projectLeadLastName: item.projectLeadLastName ?? null,
    projectLeadEmail: item.projectLeadEmail ?? null,
    accountExecutive: item.accountExecutive ?? null,
  };
}

export function mapToPhase(phase: NSwagPhaseDto | undefined): Phase | null {
  if (!phase) return null;

  return {
    id: phase.id ?? 0,
    title: phase.title ?? null,
  };
}

export function mapToProjectTask(
  task: NSwagProjectTaskDto | undefined,
): ProjectTask {
  if (!task) {
    return {
      id: 0,
      title: null,
      status: null,
      assignedResourceID: null,
      assignedResourceName: null,
      completedByResourceID: null,
      phase: null,
      isVisibleInClientPortal: null,
      startDateTime: null,
      endDateTime: null,
      completedDateTime: null,
      progress: null,
    };
  }

  return {
    id: task.id ?? 0,
    title: task.title ?? null,
    status: task.status ?? null,
    assignedResourceID: task.assignedResourceID ?? null,
    assignedResourceName: task.assignedResourceName ?? null,
    completedByResourceID: task.creatorResourceID ?? null, // NSwag uses creatorResourceID
    phase: task.phase ?? null,
    isVisibleInClientPortal: task.isVisibleInClientPortal ?? null,
    startDateTime: mapDate(task.startDateTime),
    endDateTime: mapDate(task.endDateTime),
    completedDateTime: mapDate(task.completedDateTime),
    progress: task.progress ?? null,
  };
}

export function mapToResource(
  resource: NSwagResourceDto | undefined,
): Resource {
  if (!resource) {
    return { id: 0, firstName: null, lastName: null };
  }

  return {
    id: resource.id ?? 0,
    firstName: resource.firstName ?? null,
    lastName: resource.lastName ?? null,
  };
}

export function mapNSwagResourceToResource(
  resource: NSwagResource | undefined,
): Resource {
  if (!resource) {
    return { id: 0, firstName: null, lastName: null };
  }

  return {
    id: resource.id ?? 0,
    firstName: resource.firstName ?? null,
    lastName: resource.lastName ?? null,
  };
}

export function mapToResources(
  resources: NSwagResource[] | undefined,
): Resource[] {
  if (!resources) return [];
  return resources.map(mapNSwagResourceToResource);
}

export function mapToProjectIndividual(
  response: NSwagProjectIndividualResponse | undefined,
): ProjectIndividual {
  if (!response) {
    return {
      projectId: 0,
      project: null,
      activePhase: null,
      tasks: [],
      orphanTasks: [],
      attachedResources: [],
      errorMessage: null,
    };
  }

  return {
    projectId: response.projectId ?? 0,
    project: mapToProjectInfo(response.project),
    activePhase: response.activePhase ?? null,
    tasks: (response.tasks ?? []).map(mapToProjectTask),
    orphanTasks: (response.orphanTasks ?? []).map(mapToProjectTask),
    attachedResources: (response.attachedResources ?? []).map(mapToResource),
    errorMessage: response.errorMessage ?? null,
  };
}

export function mapToProjectTaskGroup(
  group: NSwagProjectDetailResponseGroup | undefined,
): ProjectTaskGroup {
  if (!group) {
    return {
      title: null,
      projectTasks: [],
      completedTasks: null,
      totalTasks: null,
      projectStartDate: null,
      projectEndDate: null,
    };
  }

  return {
    title: group.title ?? null,
    projectTasks: (group.projectTasks ?? []).map(mapToProjectTask),
    completedTasks: group.completedTasks ?? null,
    totalTasks: group.totalTasks ?? null,
    projectStartDate: mapDate(group.projectStartDate),
    projectEndDate: mapDate(group.projectEndDate),
  };
}

export function mapToProjectDetail(
  response: NSwagProjectDetailResponse | undefined,
): ProjectDetail {
  if (!response) {
    return { projectTasksGroups: [] };
  }

  return {
    projectTasksGroups: (response.projectTasksGroups ?? []).map(
      mapToProjectTaskGroup,
    ),
  };
}

export function mapToContract(
  contract: NSwagContractDto | undefined,
): Contract | null {
  if (!contract) return null;

  return {
    id: contract.id ?? 0,
    contractName: contract.contractName ?? null,
  };
}
